import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { type Earnings, fetchEarnings } from "../api";
import { formatEur } from "../format";
import { AnimatedNumber, Skeleton } from "./ui";

/**
 * v2 "money first" row on Home: today + this month side by side,
 * values tick (AnimatedNumber), a two-tone mini bar shows the
 * earned/spent split. Magnitude is always positive and the label says
 * winst or kosten ("€ -2,69" reads as nonsense). Polls at 60 s.
 * docs/customer-app.md > Front-end > Home tab
 */

const POLL_MS = 60_000;

export function HomeEarnings() {
  const [today, setToday] = useState<Earnings | null>(null);
  const [month, setMonth] = useState<Earnings | null>(null);
  const todaySeq = useRef(0);
  const monthSeq = useRef(0);

  useEffect(() => {
    let cancelled = false;
    async function pollToday() {
      const my = ++todaySeq.current;
      try {
        const v = await fetchEarnings({ range: "today" });
        if (!cancelled && my === todaySeq.current) setToday(v);
      } catch {
        /* keep stale value; the Home header surfaces auth-level errors */
      }
    }
    async function pollMonth() {
      const my = ++monthSeq.current;
      try {
        const v = await fetchEarnings({ range: "month" });
        if (!cancelled && my === monthSeq.current) setMonth(v);
      } catch {
        /* same */
      }
    }
    pollToday();
    pollMonth();
    const a = window.setInterval(pollToday, POLL_MS);
    const b = window.setInterval(pollMonth, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(a);
      window.clearInterval(b);
    };
  }, []);

  return (
    <section className="grid grid-cols-2 gap-3">
      <EarningsCard label="Vandaag" data={today} />
      <EarningsCard label="Deze maand" data={month} />
    </section>
  );
}

function EarningsCard({
  label,
  data,
}: {
  label: string;
  data: Earnings | null;
}) {
  const navigate = useNavigate();
  const noContract = data?.contract == null;
  const net = data?.eur_net ?? 0;
  const isProfit = net >= 0;

  const earned = data?.eur_earned ?? 0;
  const spent = data?.eur_spent ?? 0;
  const total = earned + spent;
  const earnedPct = total > 0 ? (earned / total) * 100 : 0;

  return (
    <button
      type="button"
      onClick={() => navigate("/prijzen")}
      className="card pressable p-4 flex flex-col gap-1.5 text-left"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-ink-mute">{label}</span>
        {data && !noContract && (
          <span className={`text-[10px] font-bold uppercase tracking-wider ${isProfit ? "text-accent" : "text-ink-mute"}`}>
            {isProfit ? "winst" : "kosten"}
          </span>
        )}
      </div>

      {data == null ? (
        <Skeleton className="h-8 w-24 rounded-lg" />
      ) : noContract ? (
        <>
          <div className="text-lg font-semibold text-ink-mute">-</div>
          <div className="text-[10px] text-ink-mute">geen contract ingesteld</div>
        </>
      ) : (
        <>
          <AnimatedNumber
            value={Math.abs(net)}
            format={(v) => formatEur(v)}
            className={`display-num text-[26px] leading-none ${isProfit ? "text-accent" : "text-ink"}`}
          />
          {/* earned vs spent composition bar */}
          <div className="mt-1.5 h-1.5 rounded-full bg-card-soft overflow-hidden flex">
            <div
              className="h-full bg-accent transition-[width] duration-500"
              style={{ width: `${earnedPct}%` }}
            />
            <div
              className="h-full bg-grid/40 transition-[width] duration-500"
              style={{ width: `${100 - earnedPct}%` }}
            />
          </div>
          <div className="text-[10px] text-ink-mute tabular">
            {`+${formatEur(earned)} · −${formatEur(spent)}`}
          </div>
        </>
      )}
    </button>
  );
}
